<template>
  <section class="wc-task-frequency" :class="{ 'wc-task-frequency--dark': isDarkMode }">
    <div class="wc-task-frequency__header">
      <div class="wc-task-frequency__icon">
        <v-icon small color="indigo">mdi-repeat</v-icon>
      </div>
      <div>
        <div class="wc-task-frequency__title">Wiederholung & Sidebar</div>
        <div class="wc-task-frequency__subtitle">
          Bestimmt, in welchem Bereich das Ämtli in der Sidebar erscheint und wie oft es eingeplant werden soll.
        </div>
      </div>
    </div>

    <div class="wc-task-frequency__body">
      <v-select
        v-model="templateData.frequency"
        :disabled="frequencyEditingDisabled"
        :items="frequencyOptions"
        label="Wiederholung / Frequenz"
        outlined
        dense
        hide-details
        :dark="isDarkMode"
        @change="$emit('normalize')" />

      <v-alert v-if="frequencyEditingDisabled" type="warning" outlined dense class="text-caption mt-3 mb-0">
        {{ lockedMessage }}
      </v-alert>

      <template v-if="templateData.frequency === 'weekly'">
        <div class="wc-task-frequency__section-label">Mögliche Wochentage</div>
        <div class="wc-task-frequency__days">
          <v-btn
            v-for="day in weekDays"
            :key="day.value"
            small
            outlined
            :disabled="frequencyEditingDisabled || isWeekendBlocked(day.value)"
            :color="selectedWeekDays.includes(day.value) ? 'primary' : 'grey'"
            class="font-weight-bold"
            @click="$emit('toggle-weekday', day.value)">
            {{ day.text }}
          </v-btn>
        </div>
        <div v-if="weekendSkipped" class="wc-task-frequency__hint">Samstag und Sonntag sind durch die Wochenend-Regel deaktiviert.</div>
      </template>

      <template v-if="templateData.frequency === 'monthly'">
        <div class="wc-task-frequency__section-label">Mögliche Monatstage</div>
        <v-select
          v-model="templateData.frequencyConfig.daysOfMonth"
          :disabled="frequencyEditingDisabled"
          :items="monthDays"
          multiple
          chips
          deletable-chips
          dense
          outlined
          :dark="isDarkMode"
          placeholder="Monatstage wählen"
          hide-details
          @change="$emit('normalize')" />
        <div class="wc-task-frequency__hint">Die Zahl bezeichnet den Kalendertag, beispielsweise 1 für den ersten Tag des Monats.</div>
      </template>

      <div v-if="usesPeriodRules" class="wc-task-frequency__strategy">
        <div class="wc-task-frequency__section-label">Wie sollen die gewählten Tage verwendet werden?</div>
        <v-radio-group
          v-model="templateData.frequencyConfig.daySelectionMode"
          :disabled="frequencyEditingDisabled"
          hide-details
          dense
          :dark="isDarkMode"
          class="mt-0"
          @change="$emit('normalize')">
          <v-radio :label="allDaysLabel" value="all" :disabled="frequencyEditingDisabled || !hasSelectedDays" color="primary" />
          <v-radio :label="flexibleLabel" value="one" color="primary" />
        </v-radio-group>

        <v-alert v-if="!hasSelectedDays" type="info" outlined dense class="text-caption mt-2 mb-0">
          Keine festen Tage gewählt: Das System verteilt das Ämtli {{ normalizedTargetCount }}×
          {{ templateData.frequency === "weekly" ? "pro Woche" : "pro Monat" }} auf passende Arbeitstage.
        </v-alert>

        <v-text-field
          v-if="templateData.frequencyConfig.daySelectionMode === 'one' || !hasSelectedDays"
          v-model.number="templateData.targetCount"
          :disabled="frequencyEditingDisabled"
          type="number"
          min="1"
          :max="targetCountMax"
          dense
          outlined
          hide-details
          :dark="isDarkMode"
          :label="templateData.frequency === 'weekly' ? 'Häufigkeit pro Woche' : 'Häufigkeit pro Monat'"
          class="wc-task-frequency__count mt-3"
          @change="clampTargetCount" />
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "WorkCalendarTaskFrequencySettings",
  props: {
    templateData: {
      type: Object,
      required: true,
    },
    frequencyEditingDisabled: {
      type: Boolean,
      default: false,
    },
    lockedMessage: {
      type: String,
      default: "Diese Frequenz kann mit deinen aktuellen Berechtigungen nicht geändert werden.",
    },
    isDarkMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      frequencyOptions: [
        { text: "Nur manuell bei Bedarf", value: "once" },
        { text: "Täglich", value: "daily" },
        { text: "Wöchentlich", value: "weekly" },
        { text: "Monatlich", value: "monthly" },
      ],
      weekDays: [
        { text: "Mo", value: 1 },
        { text: "Di", value: 2 },
        { text: "Mi", value: 3 },
        { text: "Do", value: 4 },
        { text: "Fr", value: 5 },
        { text: "Sa", value: 6 },
        { text: "So", value: 7 },
      ],
      monthDays: Array.from({ length: 31 }, (_, index) => index + 1),
    }
  },
  computed: {
    selectedWeekDays() {
      return this.templateData.frequencyConfig && Array.isArray(this.templateData.frequencyConfig.daysOfWeek)
        ? this.templateData.frequencyConfig.daysOfWeek.map(Number)
        : []
    },
    selectedMonthDays() {
      return this.templateData.frequencyConfig && Array.isArray(this.templateData.frequencyConfig.daysOfMonth)
        ? this.templateData.frequencyConfig.daysOfMonth
        : []
    },
    usesPeriodRules() {
      return this.templateData.frequency === "weekly" || this.templateData.frequency === "monthly"
    },
    hasSelectedDays() {
      return this.templateData.frequency === "weekly" ? this.selectedWeekDays.length > 0 : this.selectedMonthDays.length > 0
    },
    weekendSkipped() {
      return Boolean(this.templateData.weekendConfig && this.templateData.weekendConfig.mode === "skip_weekend")
    },
    allDaysLabel() {
      return this.templateData.frequency === "monthly" ? "An jedem gewählten Monatstag einplanen" : "An jedem gewählten Wochentag einplanen"
    },
    flexibleLabel() {
      return this.templateData.frequency === "monthly"
        ? "Häufigkeit flexibel auf die gewählten Monatstage verteilen"
        : "Häufigkeit flexibel auf die gewählten Wochentage verteilen"
    },
    normalizedTargetCount() {
      return Math.max(1, Number(this.templateData.targetCount) || 1)
    },
    targetCountMax() {
      if (this.templateData.frequency !== "weekly") return 31
      const selectedCandidateDays = new Set(this.selectedWeekDays.filter((day) => !this.isWeekendBlocked(day)))
      return selectedCandidateDays.size || (this.weekendSkipped ? 5 : 7)
    },
  },
  watch: {
    targetCountMax() {
      if (this.templateData.frequency === "weekly") this.clampTargetCount()
    },
  },
  mounted() {
    if (this.templateData.frequency === "weekly") this.clampTargetCount()
  },
  methods: {
    isWeekendBlocked(day) {
      return day >= 6 && this.weekendSkipped
    },
    clampTargetCount() {
      const targetCount = Math.min(this.targetCountMax, Math.max(1, Number(this.templateData.targetCount) || 1))
      if (targetCount !== this.templateData.targetCount) {
        this.$set(this.templateData, "targetCount", targetCount)
      }
      this.$emit("normalize")
    },
  },
}
</script>

<style scoped>
.wc-task-frequency {
  padding: 16px;
  color: #263445;
  background: rgba(63, 81, 181, 0.035);
  border: 1px solid rgba(63, 81, 181, 0.16);
  border-radius: 14px;
}

.wc-task-frequency--dark {
  color: #f4f7fa;
  background: rgba(92, 107, 192, 0.08);
  border-color: rgba(121, 134, 203, 0.22);
}

.wc-task-frequency__header {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  margin-bottom: 14px;
}

.wc-task-frequency__icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  background: rgba(63, 81, 181, 0.11);
  border-radius: 9px;
}

.wc-task-frequency__title {
  font-size: 0.9rem;
  font-weight: 800;
}

.wc-task-frequency__subtitle,
.wc-task-frequency__hint {
  margin-top: 3px;
  color: #748194;
  font-size: 0.72rem;
  line-height: 1.4;
}

.wc-task-frequency__body {
  padding-left: 43px;
}

.wc-task-frequency__section-label {
  margin: 16px 0 8px;
  color: #748194;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.wc-task-frequency__days {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.wc-task-frequency__days .v-btn {
  min-width: 43px;
  border-radius: 8px;
  text-transform: none;
}

.wc-task-frequency__strategy {
  margin-top: 14px;
  padding-top: 1px;
  border-top: 1px solid rgba(128, 128, 128, 0.14);
}

.wc-task-frequency__count {
  max-width: 240px;
}

@media (max-width: 600px) {
  .wc-task-frequency__body {
    padding-left: 0;
  }
}
</style>
